/**
 * ThreadSearch — ONE combobox over the viewer's threads, messages, files and
 * routines (PR-C, 2026-09-09: the search that suggests; replaces the PR-5
 * ThreadSearchBox), mounted in the rail header and the mobile sheet.
 *   • BEFORE typing: "jump back in" (recent threads, unread marked), routines
 *     by next run, and the last searches;
 *   • AS YOU TYPE: grouped results — threads · messages · files · routines —
 *     with what matched highlighted; typos and any word order are fine;
 *   • the deep tier runs BY ITSELF once typing pauses ("searching
 *     everywhere…" in the messages heading) and merges by score; a failed
 *     run offers retry;
 *   • a hit opens its thread (and scrolls to the message); the query stays
 *     until × clears it; full keyboard control (↑/↓, Home/End, Enter,
 *     Escape) through useThreadSearchController.
 * Chrome copy only (D9), in the viewer's language.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import type { MatchRange, ThreadSearchHit } from '../../services/threadSearch/searchThreadCorpus';
import { threadListCopy } from './threadListCopy';
import type { ThreadListLang } from '../../types/threadCategories';
import {
  useThreadSearchController,
  type SearchGroup,
  type SearchOption,
  type ThreadSearchApi,
} from './useThreadSearchController';

export type { ThreadSearchApi, ThreadSearchSuggestions, SuggestedThread, SuggestedRoutine } from './useThreadSearchController';

export interface ThreadSearchProps {
  api: ThreadSearchApi;
  variant?: 'rail' | 'sheet';
  /** Called when a result opens (the sheet closes itself). */
  onOpened?: () => void;
  /** Focus the box on mount (the sheet opened for search, ⌘K). */
  autoFocus?: boolean;
}

const SearchWrap = styled.div`
  padding: 0 12px 6px 12px;
`;

const SearchInputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 28px 7px 30px;
  border: 1px solid rgba(198, 167, 94, 0.28);
  border-radius: 8px;
  background: rgba(198, 167, 94, 0.06);
  color: rgba(255, 255, 255, 0.92);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  outline: none;

  &::placeholder {
    color: rgba(182, 146, 76, 0.7);
  }

  &:focus {
    border-color: rgba(198, 167, 94, 0.6);
    background: rgba(198, 167, 94, 0.09);
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
`;

const SearchGlyph = styled.svg`
  position: absolute;
  left: 9px;
  width: 13px;
  height: 13px;
  pointer-events: none;
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 6px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(182, 146, 76, 0.85);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.12);
  }
`;

const ResultsPanel = styled.div`
  margin-top: 6px;
  max-height: min(52vh, 460px);
  overflow-y: auto;
  border: 1px solid rgba(198, 167, 94, 0.22);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.18);
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

const ResultsHeading = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 7px 10px 3px;
  color: rgba(182, 146, 76, 0.85);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const HeadingCount = styled.span`
  font-weight: 500;
  opacity: 0.75;
`;

const HeadingNote = styled.span`
  margin-left: auto;
  font-weight: 500;
  font-style: italic;
  opacity: 0.8;
`;

const ResultRow = styled.div<{ $active?: boolean }>`
  display: block;
  width: 100%;
  padding: 7px 10px;
  border: none;
  border-radius: 0;
  background: ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.14)' : 'transparent')};
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    background: rgba(198, 167, 94, 0.1);
  }
`;

const ResultTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultTitleText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultMeta = styled.div`
  margin-top: 1px;
  color: rgba(182, 146, 76, 0.85);
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultSnippet = styled.div`
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 11.5px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ResultNote = styled.div`
  padding: 8px 10px 9px;
  color: rgba(182, 146, 76, 0.8);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
`;

const Mark = styled.mark`
  background: rgba(198, 167, 94, 0.28);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
`;

const UnreadDot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand-gold);
`;

const RetryAction = styled.span`
  margin-left: 6px;
  color: var(--brand-gold);
  text-decoration: underline;
`;

/** The text with its matched spans wrapped in <mark>. */
export const HighlightedText: React.FC<{ text: string; ranges?: readonly MatchRange[] }> = ({ text, ranges }) => {
  if (!ranges || ranges.length === 0) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let at = 0;
  ranges.forEach((r, i) => {
    const start = Math.max(at, Math.min(r.start, text.length));
    const end = Math.max(start, Math.min(r.end, text.length));
    if (start > at) parts.push(<React.Fragment key={`t${i}`}>{text.slice(at, start)}</React.Fragment>);
    if (end > start) parts.push(<Mark key={`m${i}`} data-thread-search-mark="">{text.slice(start, end)}</Mark>);
    at = end;
  });
  if (at < text.length) parts.push(<React.Fragment key="tail">{text.slice(at)}</React.Fragment>);
  return <>{parts}</>;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** A short "when" for a result row: today's time, this week's weekday, else the date. */
export function shortWhen(iso: string | undefined, lang: ThreadListLang, now: number = Date.now()): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const locale = lang === 'es' ? 'es' : 'en';
  const d = new Date(ms);
  const today = new Date(now);
  const sameDay = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  try {
    if (sameDay) return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d);
    if (Math.abs(now - ms) < 6 * DAY_MS) return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
    if (d.getFullYear() === today.getFullYear()) return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d);
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function whoLabel(hit: ThreadSearchHit, copy: ReturnType<typeof threadListCopy>): string {
  if (hit.who) return hit.who === 'you' ? copy.you : hit.who === 'prest0n' ? copy.prest0n : hit.who;
  if (hit.isUser === true) return copy.you;
  if (hit.isUser === false) return copy.prest0n;
  return '';
}

const GROUP_LABEL: Record<SearchGroup['key'], (copy: ReturnType<typeof threadListCopy>) => string> = {
  threads: (c) => c.threadsGroup,
  messages: (c) => c.messagesGroup,
  files: (c) => c.filesGroup,
  routines: (c) => c.routinesGroup,
  'jump-back-in': (c) => c.jumpBackIn,
  'suggested-routines': (c) => c.routinesGroup,
  'recent-searches': (c) => c.recentSearches,
};

export const ThreadSearch: React.FC<ThreadSearchProps> = ({ api, variant = 'rail', onOpened, autoFocus }) => {
  const copy = threadListCopy(api.lang);
  const c = useThreadSearchController(api, { variant, onOpened });
  // The controller's stable handlers, destructured for the hooks below (the
  // exhaustive-deps idiom: a method call would ask for the whole object).
  const { open: panelOpen, close: closePanel, clear: clearQuery } = c;
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // An outside press closes the panel (the input keeps whatever it holds).
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node | null;
      if (t && wrapRef.current?.contains(t)) return;
      closePanel();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [panelOpen, closePanel]);

  const indexOf = useMemo(() => {
    const m = new Map<string, number>();
    c.options.forEach((o, i) => m.set(o.id, i));
    return m;
  }, [c.options]);

  const clear = useCallback(() => {
    clearQuery();
    inputRef.current?.focus();
  }, [clearQuery]);

  const renderOption = (option: SearchOption) => {
    const index = indexOf.get(option.id) ?? -1;
    const active = index === c.activeIndex;
    const common = {
      id: c.optionId(index),
      role: 'option' as const,
      'aria-selected': active,
      'data-active': active ? 'true' : undefined,
      $active: active,
      onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
      onMouseEnter: () => c.onHover(index),
      onClick: () => c.choose(index),
    };
    if (option.kind === 'retry') {
      return (
        <ResultRow key={option.id} {...common} data-thread-search-retry="" data-thread-search-failed="">
          <ResultNote as="span" style={{ padding: 0 }}>{copy.searchFailed}</ResultNote>
          <RetryAction>{copy.retry}</RetryAction>
        </ResultRow>
      );
    }
    if (option.kind === 'recent-search') {
      return (
        <ResultRow key={option.id} {...common} data-thread-search-suggestion="recent-search">
          <ResultTitle><ResultTitleText>{option.query}</ResultTitleText></ResultTitle>
        </ResultRow>
      );
    }
    if (option.kind === 'recent-thread') {
      return (
        <ResultRow key={option.id} {...common} data-thread-search-suggestion="recent-thread" data-thread-search-thread-id={option.thread.id}>
          <ResultTitle>
            {option.thread.unread && <UnreadDot aria-label={copy.unread} data-thread-search-unread="" />}
            <ResultTitleText>{option.thread.title}</ResultTitleText>
          </ResultTitle>
          {option.thread.lastActiveAt && <ResultMeta>{shortWhen(option.thread.lastActiveAt, api.lang)}</ResultMeta>}
        </ResultRow>
      );
    }
    if (option.kind === 'routine') {
      return (
        <ResultRow key={option.id} {...common} data-thread-search-suggestion="routine" data-thread-search-thread-id={option.routine.threadId}>
          <ResultTitle><ResultTitleText>{option.routine.name}</ResultTitleText></ResultTitle>
          {option.routine.nextRunAt && <ResultMeta>{copy.nextRun(shortWhen(option.routine.nextRunAt, api.lang))}</ResultMeta>}
        </ResultRow>
      );
    }
    const { hit } = option;
    const who = whoLabel(hit, copy);
    const when = shortWhen(hit.timestamp, api.lang);
    const meta = [who, when].filter(Boolean).join(' · ');
    return (
      <ResultRow
        key={option.id}
        {...common}
        data-thread-search-hit={hit.kind}
        data-thread-search-thread-id={hit.threadId}
        data-thread-search-message-id={hit.messageId ?? undefined}
      >
        {hit.kind === 'thread' && (
          <ResultTitle><ResultTitleText><HighlightedText text={hit.title || hit.snippet} ranges={hit.ranges} /></ResultTitleText></ResultTitle>
        )}
        {hit.kind === 'former-title' && (
          <>
            <ResultTitle><ResultTitleText>{hit.title || hit.snippet}</ResultTitleText></ResultTitle>
            <ResultSnippet title={copy.wasNamed(hit.snippet)}>{copy.wasNamedPrefix}<HighlightedText text={hit.snippet} ranges={hit.ranges} />”</ResultSnippet>
          </>
        )}
        {hit.kind === 'message' && (
          <>
            <ResultTitle><ResultTitleText>{hit.title || copy.threadsGroup}</ResultTitleText></ResultTitle>
            {meta && <ResultMeta>{meta}</ResultMeta>}
            <ResultSnippet><HighlightedText text={hit.snippet} ranges={hit.ranges} /></ResultSnippet>
          </>
        )}
        {hit.kind === 'file' && (
          <>
            <ResultTitle><ResultTitleText><HighlightedText text={hit.snippet} ranges={hit.ranges} /></ResultTitleText></ResultTitle>
            <ResultMeta>{[hit.title, meta].filter(Boolean).join(' · ')}</ResultMeta>
          </>
        )}
        {hit.kind === 'routine' && (
          <>
            <ResultTitle><ResultTitleText><HighlightedText text={hit.snippet} ranges={hit.ranges} /></ResultTitleText></ResultTitle>
            {hit.timestamp && <ResultMeta>{copy.nextRun(shortWhen(hit.timestamp, api.lang))}</ResultMeta>}
          </>
        )}
      </ResultRow>
    );
  };

  const hasHits = c.trimmed.length > 0 && c.options.some((o) => o.kind === 'hit');
  const nothing = c.trimmed.length > 0 && !hasHits && c.deepState !== 'loading' && c.deepState !== 'failed';

  return (
    <SearchWrap ref={wrapRef} data-thread-search={variant}>
      <SearchInputRow>
        <SearchGlyph viewBox="0 0 24 24" fill="none" stroke="rgba(182, 146, 76, 0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </SearchGlyph>
        <SearchInput
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label={copy.searchLabel}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={c.open}
          aria-controls={c.listboxId}
          aria-activedescendant={c.activeIndex >= 0 ? c.optionId(c.activeIndex) : undefined}
          data-thread-search-input=""
          placeholder={copy.searchPlaceholder}
          value={c.query}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => c.setQuery(e.target.value)}
          onFocus={c.onFocus}
          onKeyDown={c.onKeyDown}
        />
        {c.query && (
          <SearchClearButton type="button" aria-label={copy.clearSearch} data-thread-search-clear="" onMouseDown={(e) => e.preventDefault()} onClick={clear}>
            ×
          </SearchClearButton>
        )}
      </SearchInputRow>
      {c.open && (
        <ResultsPanel id={c.listboxId} data-thread-search-results="" role="listbox" aria-label={copy.searchLabel}>
          {c.groups.map((group, gi) => {
            const label = GROUP_LABEL[group.key](copy);
            const count = group.options.filter((o) => o.kind === 'hit').length;
            const isMessages = group.key === 'messages';
            const retryOnly = group.options.every((o) => o.kind === 'retry');
            return (
              <div key={`${group.key}:${gi}`} role="group" aria-label={label} data-thread-search-group={group.key}>
                {!retryOnly && (
                  <ResultsHeading role="presentation">
                    {label}
                    {count > 0 && <HeadingCount>· {count}</HeadingCount>}
                    {isMessages && c.deepState === 'loading' && <HeadingNote data-thread-search-loading="">{copy.searchingEverywhere}</HeadingNote>}
                  </ResultsHeading>
                )}
                {group.options.map(renderOption)}
              </div>
            );
          })}
          {c.trimmed.length > 0 && c.deepState === 'loading' && !c.groups.some((g) => g.key === 'messages') && (
            <ResultNote data-thread-search-loading="">{copy.searchingEverywhere}</ResultNote>
          )}
          {nothing && (
            <ResultNote data-thread-search-empty="">
              {c.deepState === 'done' ? copy.nothingMatchesAnywhere : copy.nothingMatches}
            </ResultNote>
          )}
        </ResultsPanel>
      )}
    </SearchWrap>
  );
};

export default ThreadSearch;
